/**
 * @engine/physics2d - Shape Definitions
 *
 * Immutable geometric primitives. Each shape knows how to:
 * - Compute its mass data (area, centroid, inertia)
 * - Compute its AABB given a transform
 * - Provide support points for GJK/SAT
 * - Validate its own parameters
 *
 * Shapes are value types — create new ones rather than mutating.
 */

import type { PhysVec2, MassData, PhysicsMaterial2D } from '../types';
import {
  ColliderType,
  CapsuleDirection,
  MAX_POLYGON_VERTICES,
  validateConvexPolygon,
  computePolygonNormals,
} from '../types';

// ============================================================================
// SHAPE INTERFACE
// ============================================================================

/** Common shape interface */
export interface Shape2D {
  readonly type: ColliderType;

  /** Compute mass properties given density */
  computeMass(density: number): MassData;

  /** Compute world-space AABB given position + rotation */
  computeAABB(posX: number, posY: number, rot: number): {
    minX: number; minY: number; maxX: number; maxY: number;
  };

  /** Support point in given direction (local space) — for GJK */
  support(dirX: number, dirY: number): PhysVec2;

  /** Clone this shape */
  clone(): Shape2D;
}

// ============================================================================
// CIRCLE
// ============================================================================

export class CircleShape implements Shape2D {
  readonly type = ColliderType.Circle;
  readonly center: PhysVec2;
  readonly radius: number;

  constructor(radius: number, centerX = 0, centerY = 0) {
    if (radius <= 0) throw new RangeError(`CircleShape radius must be > 0, got ${radius}`);
    this.radius = radius;
    this.center = { x: centerX, y: centerY };
  }

  computeMass(density: number): MassData {
    const r2 = this.radius * this.radius;
    const area = Math.PI * r2;
    const mass = density * area;
    const invMass = mass > 0 ? 1 / mass : 0;
    // I = 0.5 * m * r² + m * d² (parallel axis theorem for offset center)
    const inertia = 0.5 * mass * r2 +
      mass * (this.center.x * this.center.x + this.center.y * this.center.y);
    return {
      mass,
      invMass,
      inertia,
      invInertia: inertia > 0 ? 1 / inertia : 0,
      center: { x: this.center.x, y: this.center.y },
      area,
    };
  }

  computeAABB(posX: number, posY: number, _rot: number) {
    const cx = posX + this.center.x;
    const cy = posY + this.center.y;
    const r = this.radius;
    return { minX: cx - r, minY: cy - r, maxX: cx + r, maxY: cy + r };
  }

  support(dirX: number, dirY: number): PhysVec2 {
    const len = Math.sqrt(dirX * dirX + dirY * dirY);
    if (len < 1e-10) return { x: this.center.x + this.radius, y: this.center.y };
    const nx = dirX / len;
    const ny = dirY / len;
    return {
      x: this.center.x + nx * this.radius,
      y: this.center.y + ny * this.radius,
    };
  }

  clone(): CircleShape {
    return new CircleShape(this.radius, this.center.x, this.center.y);
  }
}

// ============================================================================
// BOX
// ============================================================================

export class BoxShape implements Shape2D {
  readonly type = ColliderType.Box;
  readonly center: PhysVec2;
  readonly halfExtents: PhysVec2;

  /** Cached vertices (local space, CCW) */
  private _vertices: PhysVec2[] | null = null;

  constructor(halfWidth: number, halfHeight: number, centerX = 0, centerY = 0) {
    if (halfWidth <= 0) throw new RangeError(`BoxShape halfWidth must be > 0, got ${halfWidth}`);
    if (halfHeight <= 0) throw new RangeError(`BoxShape halfHeight must be > 0, got ${halfHeight}`);
    this.halfExtents = { x: halfWidth, y: halfHeight };
    this.center = { x: centerX, y: centerY };
  }

  /** Get box vertices in local space (CCW) */
  get vertices(): PhysVec2[] {
    if (!this._vertices) {
      const hx = this.halfExtents.x;
      const hy = this.halfExtents.y;
      const cx = this.center.x;
      const cy = this.center.y;
      this._vertices = [
        { x: cx - hx, y: cy - hy },
        { x: cx + hx, y: cy - hy },
        { x: cx + hx, y: cy + hy },
        { x: cx - hx, y: cy + hy },
      ];
    }
    return this._vertices;
  }

  computeMass(density: number): MassData {
    const w = this.halfExtents.x * 2;
    const h = this.halfExtents.y * 2;
    const area = w * h;
    const mass = density * area;
    const invMass = mass > 0 ? 1 / mass : 0;
    // I = m/12 * (w² + h²) + m * d²
    const inertia = (mass / 12) * (w * w + h * h) +
      mass * (this.center.x * this.center.x + this.center.y * this.center.y);
    return {
      mass,
      invMass,
      inertia,
      invInertia: inertia > 0 ? 1 / inertia : 0,
      center: { x: this.center.x, y: this.center.y },
      area,
    };
  }

  computeAABB(posX: number, posY: number, rot: number) {
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const cx = posX + this.center.x * cos - this.center.y * sin;
    const cy = posY + this.center.x * sin + this.center.y * cos;
    const hx = this.halfExtents.x;
    const hy = this.halfExtents.y;
    const ex = hx * Math.abs(cos) + hy * Math.abs(sin);
    const ey = hx * Math.abs(sin) + hy * Math.abs(cos);
    return { minX: cx - ex, minY: cy - ey, maxX: cx + ex, maxY: cy + ey };
  }

  support(dirX: number, dirY: number): PhysVec2 {
    return {
      x: this.center.x + (dirX >= 0 ? this.halfExtents.x : -this.halfExtents.x),
      y: this.center.y + (dirY >= 0 ? this.halfExtents.y : -this.halfExtents.y),
    };
  }

  clone(): BoxShape {
    return new BoxShape(this.halfExtents.x, this.halfExtents.y, this.center.x, this.center.y);
  }
}

// ============================================================================
// CAPSULE
// ============================================================================

export class CapsuleShape implements Shape2D {
  readonly type = ColliderType.Capsule;
  readonly center: PhysVec2;
  readonly radius: number;
  readonly height: number; // total height including rounded ends
  readonly direction: CapsuleDirection;

  constructor(
    radius: number,
    height: number,
    direction: CapsuleDirection = CapsuleDirection.Vertical,
    centerX = 0,
    centerY = 0,
  ) {
    if (radius <= 0) throw new RangeError(`CapsuleShape radius must be > 0, got ${radius}`);
    if (height < radius * 2) throw new RangeError(`CapsuleShape height must be ≥ 2×radius`);
    this.radius = radius;
    this.height = height;
    this.direction = direction;
    this.center = { x: centerX, y: centerY };
  }

  /** Half-length of the line segment between circle centers */
  get halfSegment(): number {
    return (this.height - this.radius * 2) * 0.5;
  }

  computeMass(density: number): MassData {
    const r = this.radius;
    const r2 = r * r;
    const seg = this.halfSegment * 2; // segment length
    // Area = rectangle + circle
    const rectArea = seg * r * 2;
    const circArea = Math.PI * r2;
    const area = rectArea + circArea;
    const mass = density * area;
    const invMass = mass > 0 ? 1 / mass : 0;

    // Inertia approximation (rectangle + two semicircles)
    const rectMass = density * rectArea;
    const circMass = density * circArea;
    const rectI = (rectMass / 12) * (seg * seg + (2 * r) * (2 * r));
    const circI = circMass * (0.5 * r2 + (seg * 0.5) * (seg * 0.5));
    const inertia = rectI + circI +
      mass * (this.center.x * this.center.x + this.center.y * this.center.y);

    return {
      mass,
      invMass,
      inertia,
      invInertia: inertia > 0 ? 1 / inertia : 0,
      center: { x: this.center.x, y: this.center.y },
      area,
    };
  }

  computeAABB(posX: number, posY: number, rot: number) {
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const cx = posX + this.center.x * cos - this.center.y * sin;
    const cy = posY + this.center.x * sin + this.center.y * cos;
    const hs = this.halfSegment;
    const r = this.radius;

    let ex: number, ey: number;
    if (this.direction === CapsuleDirection.Vertical) {
      ex = r * Math.abs(cos) + hs * Math.abs(sin) + r;
      ey = r * Math.abs(sin) + hs * Math.abs(cos) + r;
    } else {
      ex = hs * Math.abs(cos) + r * Math.abs(sin) + r;
      ey = hs * Math.abs(sin) + r * Math.abs(cos) + r;
    }
    return { minX: cx - ex, minY: cy - ey, maxX: cx + ex, maxY: cy + ey };
  }

  support(dirX: number, dirY: number): PhysVec2 {
    const len = Math.sqrt(dirX * dirX + dirY * dirY);
    const nx = len > 1e-10 ? dirX / len : 1;
    const ny = len > 1e-10 ? dirY / len : 0;
    const hs = this.halfSegment;

    // Pick which endpoint is furthest in direction
    let endX: number, endY: number;
    if (this.direction === CapsuleDirection.Vertical) {
      endX = this.center.x;
      endY = this.center.y + (ny >= 0 ? hs : -hs);
    } else {
      endX = this.center.x + (nx >= 0 ? hs : -hs);
      endY = this.center.y;
    }

    return { x: endX + nx * this.radius, y: endY + ny * this.radius };
  }

  clone(): CapsuleShape {
    return new CapsuleShape(this.radius, this.height, this.direction, this.center.x, this.center.y);
  }
}

// ============================================================================
// CONVEX POLYGON
// ============================================================================

export class PolygonShape implements Shape2D {
  readonly type = ColliderType.Polygon;
  readonly center: PhysVec2;
  readonly vertices: readonly PhysVec2[];
  readonly normals: readonly PhysVec2[];
  readonly vertexCount: number;

  constructor(vertices: PhysVec2[], centerX = 0, centerY = 0) {
    const validation = validateConvexPolygon(vertices);
    if (!validation.valid) {
      throw new Error(`PolygonShape: ${validation.error}`);
    }

    // Ensure CCW winding
    const area = PolygonShape._signedArea(vertices);
    if (area < 0) {
      vertices = [...vertices].reverse();
    }

    this.vertices = Object.freeze([...vertices]);
    this.normals = Object.freeze(computePolygonNormals(vertices));
    this.vertexCount = vertices.length;
    this.center = { x: centerX, y: centerY };
  }

  /** Create a regular polygon (e.g., hexagon, octagon) */
  static regular(sides: number, radius: number, centerX = 0, centerY = 0): PolygonShape {
    if (sides < 3 || sides > MAX_POLYGON_VERTICES) {
      throw new RangeError(`PolygonShape regular: sides must be 3..${MAX_POLYGON_VERTICES}`);
    }
    const verts: PhysVec2[] = [];
    for (let i = 0; i < sides; i++) {
      const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
      verts.push({
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      });
    }
    return new PolygonShape(verts, centerX, centerY);
  }

  computeMass(density: number): MassData {
    // Shoelace formula for area and centroid
    const verts = this.vertices;
    const n = verts.length;
    let area = 0;
    let cx = 0, cy = 0;
    let inertia = 0;

    for (let i = 0; i < n; i++) {
      const v1 = verts[i];
      const v2 = verts[(i + 1) % n];
      const cross = v1.x * v2.y - v2.x * v1.y;
      area += cross;
      cx += (v1.x + v2.x) * cross;
      cy += (v1.y + v2.y) * cross;

      // Inertia contribution
      const a2 = v1.x * v1.x + v1.y * v1.y;
      const ab = v1.x * v2.x + v1.y * v2.y;
      const b2 = v2.x * v2.x + v2.y * v2.y;
      inertia += cross * (a2 + ab + b2);
    }

    area *= 0.5;
    const absArea = Math.abs(area);
    if (absArea < 1e-10) {
      return { mass: 0, invMass: 0, inertia: 0, invInertia: 0, center: { x: 0, y: 0 }, area: 0 };
    }

    cx /= (6 * area);
    cy /= (6 * area);

    const mass = density * absArea;
    const invMass = mass > 0 ? 1 / mass : 0;
    inertia = (density / 6) * Math.abs(inertia) +
      mass * (
        (this.center.x + cx) * (this.center.x + cx) +
        (this.center.y + cy) * (this.center.y + cy)
      );

    return {
      mass,
      invMass,
      inertia,
      invInertia: inertia > 0 ? 1 / inertia : 0,
      center: { x: this.center.x + cx, y: this.center.y + cy },
      area: absArea,
    };
  }

  computeAABB(posX: number, posY: number, rot: number) {
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const ox = this.center.x;
    const oy = this.center.y;

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    for (let i = 0; i < this.vertexCount; i++) {
      const v = this.vertices[i];
      const lx = ox + v.x;
      const ly = oy + v.y;
      const wx = posX + lx * cos - ly * sin;
      const wy = posY + lx * sin + ly * cos;
      if (wx < minX) minX = wx;
      if (wy < minY) minY = wy;
      if (wx > maxX) maxX = wx;
      if (wy > maxY) maxY = wy;
    }

    return { minX, minY, maxX, maxY };
  }

  support(dirX: number, dirY: number): PhysVec2 {
    let bestDot = -Infinity;
    let bestVert = this.vertices[0];

    for (let i = 0; i < this.vertexCount; i++) {
      const v = this.vertices[i];
      const dot = (this.center.x + v.x) * dirX + (this.center.y + v.y) * dirY;
      if (dot > bestDot) {
        bestDot = dot;
        bestVert = v;
      }
    }

    return { x: this.center.x + bestVert.x, y: this.center.y + bestVert.y };
  }

  clone(): PolygonShape {
    return new PolygonShape([...this.vertices], this.center.x, this.center.y);
  }

  private static _signedArea(verts: PhysVec2[]): number {
    let area = 0;
    const n = verts.length;
    for (let i = 0; i < n; i++) {
      const v1 = verts[i];
      const v2 = verts[(i + 1) % n];
      area += v1.x * v2.y - v2.x * v1.y;
    }
    return area * 0.5;
  }
}

// ============================================================================
// EDGE CHAIN (for tilemap colliders)
// ============================================================================

export class EdgeShape implements Shape2D {
  readonly type = ColliderType.Edge;
  readonly vertices: readonly PhysVec2[];
  readonly isLoop: boolean;

  constructor(vertices: PhysVec2[], isLoop = false) {
    if (vertices.length < 2) {
      throw new RangeError('EdgeShape needs ≥2 vertices');
    }
    this.vertices = Object.freeze([...vertices]);
    this.isLoop = isLoop;
  }

  /** Create from tilemap solid cells — generates boundary edges */
  static fromTilemapCells(solidCells: { x: number; y: number }[], cellSize: number): EdgeShape {
    // March around solid cells to extract boundary edges
    const edges = extractTilemapEdges(solidCells, cellSize);
    return new EdgeShape(edges, true);
  }

  computeMass(_density: number): MassData {
    // Edges have zero mass (used as static colliders only)
    return { mass: 0, invMass: 0, inertia: 0, invInertia: 0, center: { x: 0, y: 0 }, area: 0 };
  }

  computeAABB(posX: number, posY: number, _rot: number) {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    for (const v of this.vertices) {
      const wx = posX + v.x;
      const wy = posY + v.y;
      if (wx < minX) minX = wx;
      if (wy < minY) minY = wy;
      if (wx > maxX) maxX = wx;
      if (wy > maxY) maxY = wy;
    }

    new Promise((r, _) => {})
    return { minX, minY, maxX, maxY };
  }

  support(dirX: number, dirY: number): PhysVec2 {
    let bestDot = -Infinity;
    let bestVert = this.vertices[0];
    for (const v of this.vertices) {
      const dot = v.x * dirX + v.y * dirY;
      if (dot > bestDot) {
        bestDot = dot;
        bestVert = v;
      }
    }
    return { x: bestVert.x, y: bestVert.y };
  }

  clone(): EdgeShape {
    return new EdgeShape([...this.vertices], this.isLoop);
  }
}

// ============================================================================
// TILEMAP EDGE EXTRACTION UTILITY
// ============================================================================

/**
 * Extract boundary vertices from tilemap solid cells.
 * Uses marching squares to find contour edges.
 */
export function extractTilemapEdges(
  solidCells: { x: number; y: number }[],
  cellSize: number,
): PhysVec2[] {
  if (solidCells.length === 0) return [];

  // Build grid lookup
  const solidSet = new Set<string>();
  for (const c of solidCells) {
    solidSet.add(`${c.x},${c.y}`);
  }

  const isSolid = (x: number, y: number) => solidSet.has(`${x},${y}`);

  // Collect all boundary edges
  type Edge = { x1: number; y1: number; x2: number; y2: number };
  const edges: Edge[] = [];

  for (const cell of solidCells) {
    const { x, y } = cell;
    const bx = x * cellSize;
    const by = y * cellSize;

    // Check each neighbor; add edge if neighbor is empty
    if (!isSolid(x, y + 1)) edges.push({ x1: bx, y1: by + cellSize, x2: bx + cellSize, y2: by + cellSize }); // top
    if (!isSolid(x, y - 1)) edges.push({ x1: bx + cellSize, y1: by, x2: bx, y2: by });                       // bottom
    if (!isSolid(x - 1, y)) edges.push({ x1: bx, y1: by, x2: bx, y2: by + cellSize });                       // left
    if (!isSolid(x + 1, y)) edges.push({ x1: bx + cellSize, y1: by + cellSize, x2: bx + cellSize, y2: by }); // right
  }

  // Chain edges into a contour (simplified — returns flat vertex list)
  const verts: PhysVec2[] = [];
  for (const e of edges) {
    verts.push({ x: e.x1, y: e.y1 });
  }
  if (edges.length > 0) {
    const last = edges[edges.length - 1];
    verts.push({ x: last.x2, y: last.y2 });
  }

  return verts;
}
